/// <amd-module name="TFS/CreateProject/Components/ProjectVisibilityOptions" />
import * as React from "react";
import { Constants, ProjectVisibilityConstants } from "TFS/CreateProject/Constants";
import { IProjectCreationMetadataItemDescriptor } from "TFS/CreateProject/Contracts";
import * as CreateProjectResources from "TFS/CreateProject/Resources";
import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { Icon, IconSize } from "VSSUI/Icon";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { css } from "VSSUI/Util";

export interface IProjectVisibilityOptionsProps {
    disabled: boolean;
    projectVisibilityOptions: IProjectCreationMetadataItemDescriptor[];
    currentProjectVisibilityOption: IProjectCreationMetadataItemDescriptor;
    onProjectVisibilityChanged: (id: number) => void;
}

interface ProjectVisibilityLabelInfo {
    text: string;
    iconName: string;
}

export class ProjectVisibilityOptions extends VssComponent<IProjectVisibilityOptionsProps, {}> {
    public render(): JSX.Element {
        return (
            <div className={"project-visibility-container"}>
                <RadioButtonGroup
                    onSelect={this._onProjectVisibilityChanged}
                    selectedButtonId={this.props.currentProjectVisibilityOption.id}
                    text={CreateProjectResources.CreateProjectProjectVisibilitySectionHeader}
                >
                    {this._getProjectVisibilityOptions()}
                </RadioButtonGroup>
            </div>
        );
    }

    /**
     * Updates the selected project visibility data based on the selected radio button input.
     */
    private _onProjectVisibilityChanged = (buttonId: string): void => {
        let id = -1;
        const { projectVisibilityOptions } = this.props;
        for (let index = 0; index < projectVisibilityOptions.length; index++) {
            const value = projectVisibilityOptions[index];
            if (value.id === buttonId) {
                id = index;
                break;
            }
        }

        if (id > -1) {
            this.props.onProjectVisibilityChanged(id);
        }
    };

    /**
     * Get the project visibility options from state and contruct the choice group options.
     */
    private _getProjectVisibilityOptions(): JSX.Element[] {
        const visibilityMetadata = this.props.projectVisibilityOptions || [];
        const visibilityButtons = visibilityMetadata.map((value: IProjectCreationMetadataItemDescriptor) => {
            return (
                <RadioButton
                    className={"visibility-button flex-row"}
                    key={value.id}
                    id={value.id}
                    disabled={value.isDisabled}
                >
                    {this._getVisibilityContent(value)}
                </RadioButton>
            )
        })

        return visibilityButtons;
    }

    private _getVisibilityContent(buttonData: IProjectCreationMetadataItemDescriptor) {
        const { text, iconName } = this._getOptionLabelInfo(buttonData.id);

        return (
            <div className={"visibility-button-content flex-column"}>
                <Icon 
                    className={css("visibility-option-icon", !buttonData.isDisabled && "enabled", buttonData.isDisabled && "disabled")} 
                    iconName={iconName} 
                    size={IconSize.medium} 
                />
                <div className = "visibility-option-text">
                    {this._getTitleText(buttonData, text, buttonData.isDisabled)}
                    {this._getDesctiptionText(buttonData.id)}
                </div>
            </div>);
    }

    private _getOptionLabelInfo(visibility: string): ProjectVisibilityLabelInfo {
        switch (visibility) {
            case ProjectVisibilityConstants.TeamMembers:
                return {
                    text: CreateProjectResources.CreateProjectPrivateVisibilityText,
                    iconName: "Lock"
                };
            case ProjectVisibilityConstants.EveryoneInTenant:
                return {
                    text: CreateProjectResources.CreateProjectOrganizationVisibilityText,
                    iconName: "CityNext"
                };
            case ProjectVisibilityConstants.Everyone:
                return {
                    text: CreateProjectResources.CreateProjectPublicVisibilityText,
                    iconName: "Globe"
                };
            default:
                throw new Error(Utils_String.format(CreateProjectResources.CreateProjectInvalidVisibilityValue, visibility));
        }
    }

    private _getDesctiptionText(visibility: string): JSX.Element | string {
        let messageComponent: JSX.Element | string;

        switch (visibility) {
            case ProjectVisibilityConstants.Everyone:
                messageComponent = <FormatComponent format={CreateProjectResources.CreateProjectPublicVisibilityOptionDescription} />;
                break;
            case ProjectVisibilityConstants.EveryoneInTenant:
                messageComponent = CreateProjectResources.CreateProjectOrganizationVisibilityOptionDescription;
                break;
            case ProjectVisibilityConstants.TeamMembers:
                messageComponent = CreateProjectResources.CreateProjectPrivateVisibilityOptionDescription;
                break;
            default:
                throw new Error(Utils_String.format(CreateProjectResources.CreateProjectInvalidVisibilityValue, visibility));
        }

        return messageComponent;
    }

    private _getTitleText(buttonData: IProjectCreationMetadataItemDescriptor, visibilityTitle: string, isDisabled: boolean): JSX.Element {
        return (
            <div id={buttonData.id} className={css("visibility-option-title", !isDisabled && "enabled", isDisabled && "disabled")}>
                {visibilityTitle}
            </div>
        );
    }
}
